import { ArrowRight, Shield, Globe, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Widget from "./Widget";
import { Helmet } from "react-helmet-async";
import btcIcon from "@/assets/crypto/btc.svg";
import ethIcon from "@/assets/crypto/eth.svg";
import solIcon from "@/assets/crypto/sol.svg";
import maticIcon from "@/assets/crypto/matic.svg";
import baseIcon from "@/assets/chains/base.svg";
import trxIcon from "@/assets/crypto/trx.svg";
import arbitrumIcon from "@/assets/chains/arbitrum.svg";
import solanaIcon from "@/assets/chains/solana.svg";
const Hero = () => {
  const navigate = useNavigate();
  return <>
      <Helmet>
        <title>NowRamp | The Fast, Secure and Seamless Fiat-to-Crypto Gateway</title>
        <meta name="description" content="Buy crypto with credit card instantly. Enterprise-grade on-ramp and off-ramp platform for individuals and businesses. Purchase Bitcoin, Ethereum, USDT within seconds." />
      </Helmet>
      
      <section className="relative min-h-screen flex items-center overflow-hidden bg-[#030303] pt-24 pb-16" aria-label="Hero section">
        {/* Grid background with horizontal blue gradient sweep */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          {/* Base vertical gradient vignette */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#020617] via-transparent to-[#020617] opacity-80" />
          
          {/* Central blue glow - aligned with widget */}
          <div className="absolute top-1/2 right-[18%] -translate-y-1/2 w-[1100px] h-[600px] bg-blue-600/30 rounded-[999px] blur-[180px]" />
          
          {/* Purple glow - bottom right accent */}
          <div className="absolute bottom-[-10%] right-[-10%] w-[900px] h-[900px] bg-purple-700/30 rounded-full blur-[220px]" />
          
          {/* Radial dark vignette for focus */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#020617_75%)] opacity-60 mix-blend-multiply" />
          
          {/* SVG Grid with horizontal gradient - on top */}
          <svg className="absolute inset-0 w-full h-full opacity-40" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              {/* Horizontal gradient for blue sweep effect */}
              <linearGradient id="grid-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{
                stopColor: "#020617",
                stopOpacity: 0
              }} />
                <stop offset="35%" style={{
                stopColor: "#1d4ed8",
                stopOpacity: 0.45
              }} />
                <stop offset="65%" style={{
                stopColor: "#1d4ed8",
                stopOpacity: 0.35
              }} />
                <stop offset="100%" style={{
                stopColor: "#020617",
                stopOpacity: 0
              }} />
              </linearGradient>
              {/* Fine grid pattern - small squares */}
              <pattern id="fine-grid" width="8" height="8" patternUnits="userSpaceOnUse">
                <path d="M 8 0 L 0 0 0 8" fill="none" stroke="url(#grid-grad)" strokeWidth="0.4" strokeLinecap="square" />
              </pattern>
              {/* Secondary softer grid layer for depth */}
              <pattern id="fine-grid-soft" width="16" height="16" patternUnits="userSpaceOnUse">
                <path d="M 16 0 L 0 0 0 16" fill="none" stroke="rgba(15,23,42,0.35)" strokeWidth="0.4" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#fine-grid)" />
            <rect width="100%" height="100%" fill="url(#fine-grid-soft)" opacity="0.5" />
          </svg>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center max-w-7xl mx-auto">
            
            {/* Left Content */}
            <div className="text-left space-y-6">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-[#0A0A0A] border border-white/10 px-4 py-2 rounded-full">
                <Shield className="w-4 h-4 text-green-500" aria-hidden="true" />
                <span className="text-sm font-medium text-white">Settlement on</span>
                <img src={baseIcon} alt="" className="w-4 h-4" />
                <img src={arbitrumIcon} alt="" className="w-4 h-4" />
                <img src={solanaIcon} alt="" className="w-4 h-4" />
              </div>

              {/* Main heading */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight tracking-tight">
                The Fast, Secure{" "}
                <span className="bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                  Fiat-to-Crypto
                </span>
                <br />
                <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                  Gateway.
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl">
                Whether you want to buy crypto with a credit or debit card, convert fiat to Bitcoin, Ethereum or USDT — NowRamp makes it effortless.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-row gap-4 items-center">
              <button onClick={() => navigate("/buy")} className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all shadow-lg hover:shadow-blue-600/25 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-black">
                  Buy Now   
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
                <button onClick={() => navigate("/docs")} className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A0A0A] border border-white/10 text-white font-semibold rounded-lg hover:bg-[#0F0F0F] transition-all focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-black">
                  Integration API
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-4 sm:gap-6 pt-4 border-t border-white/5 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Shield className="w-4 h-4 text-green-500" aria-hidden="true" />
                  <span>SOC2 Certified</span>
                </div>
                <div className="w-px h-4 bg-white/10 hidden sm:block" aria-hidden="true" />
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Globe className="w-4 h-4 text-blue-500" aria-hidden="true" />
                  <span>150+ Countries</span>
                </div>
                <div className="w-px h-4 bg-white/10 hidden sm:block" aria-hidden="true" />
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Zap className="w-4 h-4 text-yellow-500" aria-hidden="true" />
                  <span>Instant Delivery</span>
                </div>
              </div>

              {/* Supported Networks & Chains - embedded in hero */}
              <div className="pt-8 space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-[0.2em]">
                  Supported Networks &amp; Chains
                </p>
                <div className="flex flex-wrap gap-2">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] border border-white/10 rounded-full text-xs text-slate-300">
                    <img src={btcIcon} alt="Bitcoin" className="w-4 h-4" />
                    Bitcoin
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] border border-white/10 rounded-full text-xs text-slate-300">
                    <img src={ethIcon} alt="Ethereum" className="w-4 h-4" />
                    Ethereum
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] border border-white/10 rounded-full text-xs text-slate-300">
                    <img src={solIcon} alt="Solana" className="w-4 h-4" />
                    Solana
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] border border-white/10 rounded-full text-xs text-slate-300">
                    <img src={maticIcon} alt="Polygon" className="w-4 h-4" />
                    Polygon
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] border border-white/10 rounded-full text-xs text-slate-300">
                    <img src={baseIcon} alt="Base" className="w-4 h-4" />
                    Base
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] border border-white/10 rounded-full text-xs text-slate-300">
                    <img src={trxIcon} alt="Tron" className="w-4 h-4" />
                    Tron
                  </div>
                </div>
              </div>
            </div>

            {/* Right Widget */}
            <div className="w-full flex justify-center lg:justify-end">
              <div className="relative">
                {/* Gradient blue to purple shadow with pulse animation */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/40 to-purple-600/40 blur-[80px] -z-10 scale-110" aria-hidden="true" />
                <Widget />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
};
export default Hero;